if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.submitLockActive = false;
        this.hasGlobalLockListener = false;
        this.onGlobalAtcLockChange = this.onGlobalAtcLockChange.bind(this);

        this.form = this.querySelector("form");
        if (this.form) {
          this.handleFormSubmit();
        } else {
          this.createFormElement();
        }

        this.cartDrawer = document.getElementById("CartDrawer");
      }

      handleFormSubmit() {
        if (this.variantIdInput) {
          this.variantIdInput.disabled = false;
        }
        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector("span");
        this.refreshSubmitButtonText();
        this.storeSubmitButtonDefaultLabel();

        if (!this.hasGlobalLockListener) {
          document.addEventListener("hb:atc-lock-change", this.onGlobalAtcLockChange);
          this.hasGlobalLockListener = true;
        }
        this.onGlobalAtcLockChange();

        if (document.querySelector("cart-drawer")) {
          this.submitButton?.setAttribute("aria-haspopup", "dialog");
        }

        this.hideErrors = this.dataset.hideErrors === "true";

        // bind once
        this._boundSubmit = this.onSubmitHandler.bind(this);
        this.form.addEventListener("submit", this._boundSubmit, { passive: false });
      }

      createFormElement() {
        const productForm = this;
        const formId = productForm.dataset.formId;

        if (productForm) {
          const errorWrapper = document.querySelector(`.product-form__error-message-wrapper[data-form-id="${formId}"]`);

          const form = document.createElement("form");
          form.setAttribute("method", "post");
          form.setAttribute("action", "/cart/add");
          form.setAttribute("id", productForm.dataset.formId);
          form.setAttribute("accept-charset", "UTF-8");
          form.setAttribute("class", "form");
          form.setAttribute("enctype", "multipart/form-data");
          form.setAttribute("novalidate", "novalidate");
          form.setAttribute("data-type", "add-to-cart-form");

          if (errorWrapper) {
            const errorParent = errorWrapper.parentElement;
            errorParent.removeChild(errorWrapper);
          }

          while (productForm.firstChild) form.appendChild(productForm.firstChild);

          if (errorWrapper) {
            const errorParent = errorWrapper.parentElement;
            errorParent.appendChild(errorWrapper);
          }

          productForm.appendChild(form);

          this.form = this.querySelector("form");
          if (this.form) {
            this.handleFormSubmit();
          }
        }
      }

      // util fetch with timeout
      async fetchWithTimeout(input, init, ms = 8000) {
        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), ms);
        try {
          const res = await fetch(input, { ...init, signal: controller.signal });
          clearTimeout(id);
          return res;
        } catch (e) {
          clearTimeout(id);
          throw e;
        }
      }

      async fetchCart() {
        try {
          const cartJsonUrl = `${routes.cart_url}.js`;
          const res = await this.fetchWithTimeout(cartJsonUrl, { headers: { Accept: "application/json" } }, 6000);
          return await res.json();
        } catch (err) {
          console.error("Error fetching cart:", err);
          return null;
        }
      }

      validateMaxQuantityWithCartSnapshot(formData, formElement, cart) {
        let errorMessage = "";
        const quantityToAdd = Number(formData.get("quantity")) || 1;

        const maxQuantityInput =
          formElement.querySelector('input[name="max-quantity"]') ||
          formElement.querySelector('input[name="max-quantity--quickview"]');
        const maxQuantity = maxQuantityInput && maxQuantityInput.value != "" ? Number(maxQuantityInput.value) : null;

        const invQuantityInput =
          formElement.querySelector('input[name="inventory-quantity"]') ||
          formElement.querySelector('input[name="inventory-quantity--quickview"]');
        const invQuantity = invQuantityInput ? Number(invQuantityInput.value) : null;

        const variantIdInput = formElement.querySelector('input[name="id"]');
        const variantId = variantIdInput ? variantIdInput.value : null;

        if (!variantId || maxQuantity === null || isNaN(maxQuantity) || !cart) return false;

        const cartItem = cart?.items?.find((item) => item?.variant_id == variantId);
        const currentQuantityInCart = cartItem ? cartItem?.quantity : 0;

        if (invQuantity != null && currentQuantityInCart >= invQuantity) {
          errorMessage = window.cartStrings.outOfStockError;
          this.handleErrorMessage(errorMessage);
          return true;
        }

        if (currentQuantityInCart >= maxQuantity) {
          errorMessage = window.cartStrings.maxInCartError;
          this.handleErrorMessage(errorMessage);
          return true;
        }

        if (quantityToAdd + currentQuantityInCart > maxQuantity) {
          const availableToAdd = maxQuantity - currentQuantityInCart;
          errorMessage = window.cartStrings.limitExceededError.replace("[availableToAdd]", availableToAdd);
          this.handleErrorMessage(errorMessage);
          return true;
        }

        return false;
      }

      refreshSubmitButtonText() {
        if (!this.submitButton) return null;
        if (this.submitButtonText && this.submitButtonText?.isConnected) return this.submitButtonText;
        const span = this.submitButton.querySelector("span");
        if (span) {
          this.submitButtonText = span;
          return span;
        }
        this.submitButtonText = null;
        return null;
      }

      readSubmitButtonText() {
        if (!this.submitButton) return "";
        const labelEl = this.refreshSubmitButtonText();
        return labelEl ? labelEl.textContent : this.submitButton.textContent || "";
      }

      writeSubmitButtonText(value) {
        if (!this.submitButton) return;
        const labelEl = this.refreshSubmitButtonText();
        if (labelEl) {
          labelEl.textContent = value;
        } else {
          const span = document.createElement("span");
          span.textContent = value;
          this.submitButton.textContent = "";
          this.submitButton.appendChild(span);
          this.submitButtonText = span;
        }
      }

      storeSubmitButtonDefaultLabel() {
        if (!this.submitButton) return;
        const label = this.readSubmitButtonText();
        this.submitButton.dataset.defaultLabel = label;
        this.submitButton.dataset.defaultLabelClean = label.trim().replace(/\u2026$|\.\.\.$/, "");
      }

      setSubmitButtonLoadingState(isLoading) {
        if (!this.submitButton) return;
        if (isLoading) {
          if (!this.submitLockActive) {
            this.storeSubmitButtonDefaultLabel();
            this.submitLockActive = true;
            window.__hbAtcLock?.increment?.();
          }
          const loadingText = window.variantStrings?.adding;
          this.writeSubmitButtonText(loadingText);
          this.submitButton.classList.add("product-form__submit--loading", "product-form__submit--locked");
        } else {
          if (this.submitLockActive) {
            this.submitLockActive = false;
            window.__hbAtcLock?.decrement?.();
          }
          const original = this.submitButton.dataset.defaultLabel;
          if (original) this.writeSubmitButtonText(original);
          this.submitButton.classList.remove("product-form__submit--loading", "product-form__submit--locked");
        }
      }

      disableATCButtons() {
        const clickedBtn = this.submitButton;
        const predictiveScope =
          clickedBtn?.closest?.(".predictive-search") || clickedBtn?.closest?.("predictive-search");

        this._atcScopeEl = predictiveScope || document;

        const btns = this._atcScopeEl.querySelectorAll("button.product-form__submit");
        btns.forEach((btn) => {
          if (!btn) return;
          if (predictiveScope && btn === clickedBtn) return;
          if (!btn.hasAttribute("disabled")) {
            btn.dataset.atcTempDisabled = "1";
            btn.setAttribute("disabled", "");
            btn.setAttribute("aria-disabled", "true");
            btn.classList.add("product-form__submit--locked");
          }
        });
      }

      enableATCButtons() {
        const scope = this._atcScopeEl || document;
        scope.querySelectorAll("button.product-form__submit[data-atc-temp-disabled]").forEach((btn) => {
          btn.removeAttribute("disabled");
          btn.removeAttribute("aria-disabled");
          btn.classList.remove("product-form__submit--locked");
          delete btn.dataset.atcTempDisabled;
        });
        this._atcScopeEl = null;
      }

      setDkSilent(on) {
        window.__dkCartSilent = !!on;
      }

      async runDkSilent(cb) {
        this.setDkSilent(true);
        try {
          return await cb();
        } finally {
          setTimeout(() => this.setDkSilent(false), 80);
        }
      }

      getVariantQuantity(cart, variantId) {
        if (!variantId || !cart?.items?.length) return 0;
        const found = cart.items.find((item) => item?.variant_id == variantId);
        return found ? Number(found.quantity) || 0 : 0;
      }

      async parallelAddToCart(config) {
        const addUrl = `${routes.cart_add_url}?dk_silent=1`;
        const silentConfig = { ...config, headers: { ...config.headers, "X-DK-Silent": "1" } };
        const res = await this.fetchWithTimeout(addUrl, silentConfig, 8000);
        return res.json();
      }

      buildCartItemFromFormData(formData) {
        const item = {
          id: formData.get("id"),
          quantity: Number(formData.get("quantity")) || 1,
        };

        const properties = {};

        formData.forEach((value, key) => {
          if (key.startsWith("properties[")) {
            const propName = key.slice("properties[".length, -1);
            properties[propName] = value;
          }
        });
        if (Object.keys(properties).length) {
          item.properties = properties;
        }

        return item;
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        if (this.cart?.tagName === "CART-NOTIFICATION" && typeof this.cart.close === "function") {
          this.cart.close();
        }

        this.submitButton.setAttribute("aria-disabled", "true");
        this.disableATCButtons();
        this.setSubmitButtonLoadingState(true);

        await window.__cartQueue.enqueue(async () => {
        this.toggleCartOverlay(true);
        this.handleErrorMessage();

        const formData = new FormData(this.form);
        const addedVariantId = formData.get("id");

        const cartRemoveItems = document.querySelectorAll("cart-remove-button");
        cartRemoveItems.forEach((i) => (i.style.pointerEvents = "none"));

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";

        const sectionsToRender =
          this.cart && typeof this.cart.getSectionsToRender === "function"
            ? this.cart.getSectionsToRender().map((s) => s.section || s.id)
            : [];

        const cartPromise = this.fetchCart();

        let cartSnapshot = null;
        let cartStateForUi = null;
        try {
          cartSnapshot = await cartPromise;
          cartStateForUi = cartSnapshot;
          const offlineRemoval = await removeOfflineCartItems();
          if (offlineRemoval.removed) {
            cartSnapshot = await this.fetchCart();
            cartStateForUi = cartSnapshot;
          }

          const hasValidationError = this.validateMaxQuantityWithCartSnapshot(formData, evt.target, cartSnapshot);
          if (hasValidationError) {
            this.setSubmitButtonLoadingState(false);
            this.submitButton.removeAttribute("aria-disabled");
            this.enableATCButtons();
            this.toggleCartOverlay(false);
            cartRemoveItems.forEach((i) => (i.style.pointerEvents = "auto"));
            return;
          }
        } catch (e) {
          // if cart snapshot fails, continue and let server guard quantities
          console.warn("Cart snapshot failed, proceeding", e);
        }

        const mainItem = this.buildCartItemFromFormData(formData);
        const itemsPayload = [mainItem];

        config.body = JSON.stringify({
          items: itemsPayload,
          sections: sectionsToRender,
          sections_url: window.location.pathname,
        });

        let response;
        this.error = false;
        try {
          response = await this.runDkSilent(() => this.parallelAddToCart(config));
        } catch (e) {
          console.error(e);
          response = { status: 500, message: "Network error" };
        }

        let handled422 = false;

        if (response?.status === 422) {
          const availabilityMessage = response?.description || response?.message || "";
          try {
            const updatedState = await updateCartUI(false, cartSnapshot);
            if (updatedState) {
              response = updatedState;
              const cartChanged = hasCartChanged(cartSnapshot, updatedState);
              if (cartChanged) handled422 = true;
              cartStateForUi = updatedState || cartSnapshot;
            }
          } catch (err) {
            console.warn("Silent cart refresh after 422 failed:", err);
          }

          if (availabilityMessage) {
            this.handleErrorMessage(availabilityMessage);
          }
        }

        // handle errors fast
        if (response?.status && !handled422) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: "product-form",
            productVariantId: formData.get("id"),
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(response.description);

          if (response?.status == 422 && response?.message?.includes(window.variantStrings.hazmatRestrictedMessage)) {
            this.handleErrorMessage(response.message);
            updateCartUI();
          } else if (response?.status == 422) {
            updateCartUI();
          } else {
            const soldOutMessage = this.submitButton.querySelector(".sold-out-message");
            if (soldOutMessage) {
              this.submitButton.setAttribute("aria-disabled", true);
              const labelEl = this.refreshSubmitButtonText();
              labelEl?.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
            }
          }

          if (!this.cart) {
            window.location = window.routes.cart_url;
          }

          // finish
          await this.finishSubmit(cartRemoveItems);
          return;
        }

        // update PDP quantity roles quickly
        const productWrapper = evt.target.closest(".product-page");
        try {
          const quantityRolesData =
            cartStateForUi && addedVariantId
              ? { quantity: this.getVariantQuantity(cartStateForUi, addedVariantId), variant_id: addedVariantId }
              : response;
          updateQuantityRolesPDP(productWrapper, quantityRolesData);
        } catch {}

        // render the main add result immediately
        const cartChangedSinceSnapshot = cartSnapshot && response ? hasCartChanged(cartSnapshot, response) : true;
        // ensure the cart-notification heading shows the default "Item added" string
        window.__pdpInlineHeadingOverride = null;
        if (!this.error && cartChangedSinceSnapshot && !handled422) {
          if (this.closest("quick-add-modal")) {
            const quickAddModal = this.closest("quick-add-modal");
            document.body.addEventListener(
              "modalClosed",
              () => {
                queueMicrotask(() => this.cart.renderContents(response));
              },
              { once: true },
            );
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        }

        // notify the PDP inline quantity selector (only present on the main PDP ATC)
        try {
          const inlineQty = evt.target.querySelector("pdp-inline-quantity");
          if (inlineQty) {
            const addedLine = Array.isArray(response?.items)
              ? response.items.find((i) => String(i?.variant_id) === String(addedVariantId)) || response.items[0]
              : response;

            const detail = { variantId: addedVariantId };
            if (addedLine && typeof addedLine.quantity === "number") {
              detail.quantity = addedLine.quantity;
              detail.key = addedLine.key || "";
            } else {
              const prevQty =
                cartStateForUi && typeof this.getVariantQuantity === "function"
                  ? this.getVariantQuantity(cartStateForUi, addedVariantId)
                  : 0;
              detail.quantity = prevQty + 1;
            }

            document.dispatchEvent(
              new CustomEvent("pdp-inline-quantity:sync", { detail }),
            );
          }
        } catch {}

        // post publish without blocking
        try {
          const publishCartData = response?.sections ? response : cartStateForUi || response;
          publish?.(PUB_SUB_EVENTS?.cartUpdate, {
            source: "product-form",
            productVariantId: formData.get("id"),
            cartData: publishCartData,
          });

          document.dispatchEvent(
            new CustomEvent("cart:predictive-add", {
              detail: { variantId: formData.get("id"), source: "predictive-search" },
            }),
          );

          queueMicrotask(() => {
            try {
              window.DiscountKit?.refresh?.();
              window.discountKit?.refresh?.();
              publish?.(PUB_SUB_EVENTS?.cartUpdate, { source: "product-form-post-silent" });
            } catch {}
          });
        } catch {}

        // finish
        await this.finishSubmit(cartRemoveItems);
        }); 
      }

      async finishSubmit(cartRemoveItems) {
        this.setSubmitButtonLoadingState(false);
        this.enableATCButtons();
        if (this.cart && this.cart.classList.contains("is-empty")) this.cart.classList.remove("is-empty");
        if (!this.error) this.submitButton.removeAttribute("aria-disabled");
        cartRemoveItems.forEach((i) => (i.style.pointerEvents = "auto"));
        try {
          await window.waitForGiftCartIfBusy?.();
        } finally {
          this.toggleCartOverlay(false);
        }
      }

      handleErrorMessage(errorMessage = false) {
        const productForm = this;
        const formId = productForm.dataset.formId;
        const dlProductId = productForm.getAttribute("dl-product-id");
        const quickAddForm = this.closest(".product-actions__wrapper.quick-add-only");

        if (this.hideErrors) return;

        const isMainProduct = this.closest(".main-product");
        const isFeaturedCard = this.closest(".feature-card_product_buy-button-container");
        const isCartPagePickerCard = this.closest(".cart-page-picker__product-card");
        const isstickyAddToCart = this.closest(".add-to-cart-panel");

        if (isMainProduct) {
          const isQuickAdd = isMainProduct.querySelector(".quick-add-to-bag");
          if (isQuickAdd) {
            this.errorMessageWrapper = quickAddForm?.querySelector(
              `.product-form__error-message-wrapper[data-form-id="${formId}"]`,
            );
          } else {
            this.errorMessageWrapper = isMainProduct.querySelector(".product-form__error-message-wrapper.pdp");
          }
        } else if (isFeaturedCard) {
          this.errorMessageWrapper = productForm
            .closest(".feature-card_product_buy-button-container")
            ?.querySelector(`.product-form__error-message-wrapper[dl-product-id="${dlProductId}"]`);
        }
        if (isstickyAddToCart) {
          this.errorMessageWrapper =
            this.errorMessageWrapper ||
            document.querySelector('.product-form__error-message-wrapper[data-form-id="sticky-add-to-cart-form"]');
        } else {
          this.errorMessageWrapper =
            this.errorMessageWrapper ||
            document.querySelector(`.product-form__error-message-wrapper[data-form-id="${formId}"]`);
        }

        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector(".product-form__error-message");

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (this._errorMessageTimeout) {
          clearTimeout(this._errorMessageTimeout);
          this._errorMessageTimeout = null;
        }

        if (this.errorMessageWrapper) {
          this.errorMessageWrapper.classList.remove("fade-out");
        }

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
          if (this.errorMessageWrapper.classList.contains("pdp")) {
            this.errorMessageWrapper.scrollIntoView({ behavior: "smooth", block: "center" });
          }
          if (isFeaturedCard || (isCartPagePickerCard && window.matchMedia("(max-width: 989px)").matches)) {
            this._errorMessageTimeout = setTimeout(() => {
              if (this.errorMessageWrapper) {
                this.errorMessageWrapper.classList.add("fade-out");
                setTimeout(() => {
                  if (this.errorMessageWrapper) {
                    this.errorMessageWrapper.toggleAttribute("hidden", true);
                    this.errorMessageWrapper.classList.remove("fade-out");
                  }
                }, 300);
              }
              this._errorMessageTimeout = null;
            }, 3000);
          }
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (this.submitButton?.hasAttribute("preorder")) {
            this.writeSubmitButtonText("Pre-Order");
          }
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButton.removeAttribute("aria-disabled");
          this.writeSubmitButtonText(window.variantStrings.addToCart);
        }

        if (this.submitButton?.hasAttribute("preorder") && disable) {
          this.writeSubmitButtonText("Out Of Stock");
        } else if (text) {
          this.writeSubmitButtonText(text);
        } else {
          this.writeSubmitButtonText(window.variantStrings.addToCart);
        }

        this.storeSubmitButtonDefaultLabel();
      }

      toggleCartOverlay(isLoading) {
        const cartItems = document.querySelector("cart-items");
        const isCartPage = window.location.pathname.includes("/cart");

        if (isCartPage && cartItems && typeof cartItems.toggleOverlay === "function") {
          cartItems.toggleOverlay(isLoading);
          return;
        }

        if (this.cartDrawer) {
          if (isLoading) this.cartDrawer.classList.add("cart-processing");
          else this.cartDrawer.classList.remove("cart-processing");
        }
      }

      get variantIdInput() {
        return this.form?.querySelector("[name=id]");
      }

      disconnectedCallback() {
        if (this.hasGlobalLockListener) {
          document.removeEventListener("hb:atc-lock-change", this.onGlobalAtcLockChange);
          this.hasGlobalLockListener = false;
        }
        if (this._boundSubmit) {
          this.form?.removeEventListener("submit", this._boundSubmit);
        }
      }

      onGlobalAtcLockChange(event) {
        if (!this.submitButton) return;
        const locked =
          typeof event?.detail?.locked === "boolean" ? event.detail.locked : Boolean(window.__hbAtcLock?.count);
        const alreadyDisabled =
          this.submitButton.hasAttribute("disabled") || this.submitButton.getAttribute("aria-disabled") === "true";

        if (locked) {
          if (!this.submitButton.dataset.atcGlobalLocked) {
            this.submitButton.dataset.atcGlobalLocked = "true";
            this.submitButton.dataset.atcGlobalPrevDisabled = alreadyDisabled ? "true" : "false";
          }
          if (!this.submitButton.dataset.defaultLabel) {
            this.storeSubmitButtonDefaultLabel();
          }
          if (!alreadyDisabled) {
            this.submitButton.setAttribute("disabled", "");
            this.submitButton.setAttribute("aria-disabled", "true");
            this.submitButton.classList.add("product-form__submit--locked");
            if (this.submitLockActive) {
              this.submitButton.classList.add("product-form__submit--loading");
              const loadingText = window.variantStrings?.adding;
              this.writeSubmitButtonText(loadingText);
            } else {
              this.submitButton.classList.remove("product-form__submit--loading");
              const defaultLabel = this.submitButton.dataset.defaultLabel;
              if (defaultLabel) this.writeSubmitButtonText(defaultLabel);
            }
          }
        } else if (this.submitButton.dataset.atcGlobalLocked === "true") {
          const wasDisabled = this.submitButton.dataset.atcGlobalPrevDisabled === "true";
          delete this.submitButton.dataset.atcGlobalLocked;
          delete this.submitButton.dataset.atcGlobalPrevDisabled;

          this.submitButton.classList.remove("product-form__submit--locked");
          if (!this.submitLockActive) {
            this.submitButton.classList.remove("product-form__submit--loading");
          }

          if (!wasDisabled && !this.submitLockActive && !this.submitButton.dataset.atcTempDisabled) {
            this.submitButton.removeAttribute("disabled");
            this.submitButton.removeAttribute("aria-disabled");
            const original = this.submitButton.dataset.defaultLabel;
            this.writeSubmitButtonText(original || this.readSubmitButtonText());
            this.storeSubmitButtonDefaultLabel();
          }
        }
      }
    },
  );
}
